import { NgClass } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { SynIconComponent } from '@synergy-design-system/angular';

import { ValidationErrors } from '@angular/forms';

export const PASSWORD_POLICY = {
  hasUpperCase: (password: string) => /[A-Z]/.test(password),
  hasLowerCase: (password: string) => /[a-z]/.test(password),
  hasNonWordCharacterOrDigit: (password: string) => /\W|\d/.test(password),
  hasMinLength: (password: string) => password.length >= 8,
} as const;

export type PasswordRequirement = keyof typeof PASSWORD_POLICY;
export const PASSWORD_REQUIREMENTS = Object.keys(PASSWORD_POLICY) as PasswordRequirement[];

export type PasswordPolicyValidationErrors = {
  [key in keyof typeof PASSWORD_POLICY]: boolean;
} & ValidationErrors;

/**
 * Validator that checks if the password meets the password policy.
 */
export function passwordPolicyValidator(value: string): PasswordPolicyValidationErrors | null {
  const password = value;

  const validationErrors = {
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNonWordCharacterOrDigit: /\W|\d/.test(password),
    hasMinLength: password.length >= 8,
  };

  const isValid = Object.values(validationErrors).every((value) => value);

  return isValid ? null : validationErrors;
}

@Component({
  selector: 'app-theme-password-requirements',
  standalone: true,
  imports: [NgClass, SynIconComponent],
  template: `
    @if (displayPasswordRequirements()) {
      <div class="password-requirements">
      <span class="password-requirement-title" i18n="@@password-requirements"
      >Password requirements:</span
      >
        @for (requirement of passwordRequirements(); track $index) {
          <div class="password-requirement" [ngClass]="{ valid: requirement.valid }"
               [attr.data-e2e]="'password-validation-' + requirement.key">
            <span><syn-icon [name]="requirement.valid ? 'check_fill' : 'close_fill'" class="icon" /></span>
            {{ getPasswordRequirementMessage(requirement.key) }}
          </div>
        }
        <span aria-live="polite" class="visuallyhidden">
        @if (unmetRequirementsMessages().length > 0) {
          {{ messages.unmetRequirements }} {{ unmetRequirementsMessages().join(', ') }}.
        } @else {
          {{ messages.allRequirementsMet }}
        }
        </span>
      </div>
    }
  `,
  styles: `
    .password-requirements {
      display: flex;
      flex-direction: column;
      margin-block-start: var(--syn-spacing-x-large);
    }

    .password-requirement-title {
      margin-block-end: var(--syn-spacing-small);
      font-weight: bold;
    }

    .password-requirement {
      display: flex;
      flex-direction: row;
      align-items: center;
      column-gap: var(--syn-spacing-x-small);
      color: var(--syn-input-help-text-color-error);

      &:is(.valid) {
        color: var(--syn-color-success-600);
      }
    }

    .icon {
      vertical-align: middle;
    }
  `,
})
export class PasswordRequirementsComponent {
  passwordRequirementMessages: Record<PasswordRequirement, string> = {
    hasMinLength: $localize`:@@password-validators.has-min-length:At least 8 characters`,
    hasLowerCase: $localize`:@@password-validators.has-lower-case:At least one lower case letter (a-z)`,
    hasUpperCase: $localize`:@@password-validators.has-upper-case:At least one upper case letter (A-Z)`,
    hasNonWordCharacterOrDigit: $localize`:@@password-validators.has-non-word-character:At least one number (0-9) or a symbol`,
  };

  messages = {
    unmetRequirements: $localize`:@@unmet-requirements:The following requirements are not met:`,
    allRequirementsMet: $localize`:@@all-requirements-met:All requirements are met.`,
  };

  password = input('');
  alwaysShowRequirements = input(false);

  displayPasswordRequirements = computed(() => {
    return this.alwaysShowRequirements() || this.password().length > 0;
  });

  passwordRequirements = computed(() => {
    const validationErrors = passwordPolicyValidator(this.password());

    return PASSWORD_REQUIREMENTS.map((key: string) => ({
      key: key as PasswordRequirement,
      valid: validationErrors?.[key] ?? true,
    }));
  });

  unmetRequirementsMessages = computed(() => {
    return this.passwordRequirements()
      .filter((requirement) => !requirement.valid)
      .map((requirement) => this.getPasswordRequirementMessage(requirement.key));
  });

  public isValid = computed(() => {
    return this.passwordRequirements().every(
      (requirement) => requirement.valid);
  });

  getPasswordRequirementMessage(requirement: PasswordRequirement) {
    return this.passwordRequirementMessages[requirement];
  }
}
